import React, { useState, useEffect, useRef } from 'react';
import {
  FaSearch,
  FaPlus,
  FaUser,
  FaCalendar,
  FaTimes,
  FaBuilding,
  FaIdCard,
  FaChevronDown,
  FaHistory,
  FaUserCheck,
  FaSpinner,
  FaCheckCircle,
  FaInfoCircle,
  FaBriefcase,
  FaRupeeSign,
  FaExclamationTriangle,
  FaPercent,
} from 'react-icons/fa';
import ReservationTable from './ReservationTable';
import { Link, useNavigate } from 'react-router-dom';
import 'react-calendar/dist/Calendar.css';
import { toast, ToastContainer } from 'react-toastify';
import { FiX, FiMail, FiPhone } from 'react-icons/fi';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

// Discount mapping based on contract ID
const getDiscountPercentage = (contractId) => {
  if (!contractId) return 0;

  // Extract the number from contract ID (e.g., PECDIS_025 -> 25)
  const match = contractId.match(/PECDIS_(\d{2,3})/);
  if (!match) return 0;

  const discountValue = parseInt(match[1]);
  // Validate discount range (1-50%)
  if (discountValue >= 1 && discountValue <= 50) {
    return discountValue;
  }
  return 0;
};

const ReservationSearch = () => {
  const [lastNameQuery, setLastNameQuery] = useState('');
  const [bookingIdQuery, setBookingIdQuery] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  const [showDateDropdown, setShowDateDropdown] = useState(false);
  const [showWalkinModal, setShowWalkinModal] = useState(false);
  const [walkinLoading, setWalkinLoading] = useState(false);
  const [walkinResponse, setWalkinResponse] = useState(null);
  const [availableRoomTypes, setAvailableRoomTypes] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [companySearchTerm, setCompanySearchTerm] = useState('');
  const companyInputRef = useRef(null);
  const dropdownRef = useRef(null);
  const dateDropdownRef = useRef(null);

  // Guest Search States
  const [showGuestSearch, setShowGuestSearch] = useState(false);
  const [guestSearchTerm, setGuestSearchTerm] = useState('');
  const [guests, setGuests] = useState([]);
  const [filteredGuests, setFilteredGuests] = useState([]);
  const [guestSearchLoading, setGuestSearchLoading] = useState(false);
  const [selectedGuest, setSelectedGuest] = useState(null);
  const guestSearchRef = useRef(null);

  // Availability States
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [availabilityData, setAvailabilityData] = useState([]);
  const [showAvailability, setShowAvailability] = useState(false);
  const [availabilityChecked, setAvailabilityChecked] = useState(false);

  // Validation Error States
  const [errors, setErrors] = useState({});

  // Confirmation Popup States
  const [showConfirmation, setShowConfirmation] = useState(false);

  const [walkinFormData, setWalkinFormData] = useState({
    firstname: '',
    lastname: '',
    phonenumber: '',
    emailid: '',
    countrycode: '+91',
    clienttype: 'Leisure',
    companyid: '',
    companyname: '',
    contractid: '',
    country: 'India',
    checkindate: '',
    checkoutdate: '',
    room_type: '',
    number_of_guests: 1,
    quantity: 1,
    special_requests: '',
    gst_number: '',
  });

  const navigate = useNavigate();

  // Date filter options
  const dateFilterOptions = [
    { value: 'all', label: 'All Time' },
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'this_week', label: 'This Week' },
    { value: 'last_week', label: 'Last Week' },
    { value: 'this_month', label: 'This Month' },
    { value: 'last_month', label: 'Last Month' },
  ];

  const getDateRange = (filter) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startDate = new Date(today);
    const endDate = new Date(today);

    switch (filter) {
      case 'today':
        return { start: today, end: new Date(today.getTime() + 86400000 - 1) };
      case 'yesterday':
        startDate.setDate(startDate.getDate() - 1);
        endDate.setDate(endDate.getDate() - 1);
        endDate.setHours(23, 59, 59, 999);
        return { start: startDate, end: endDate };
      case 'this_week': {
        const day = today.getDay();
        const diff = today.getDate() - day + (day === 0 ? -6 : 1);
        startDate.setDate(diff);
        startDate.setHours(0, 0, 0, 0);
        endDate.setDate(startDate.getDate() + 6);
        endDate.setHours(23, 59, 59, 999);
        return { start: startDate, end: endDate };
      }
      case 'last_week': {
        const day = today.getDay();
        const diff = today.getDate() - day + (day === 0 ? -6 : 1) - 7;
        startDate.setDate(diff);
        startDate.setHours(0, 0, 0, 0);
        endDate.setDate(startDate.getDate() + 6);
        endDate.setHours(23, 59, 59, 999);
        return { start: startDate, end: endDate };
      }
      case 'this_month': {
        startDate.setDate(1);
        startDate.setHours(0, 0, 0, 0);
        endDate.setMonth(today.getMonth() + 1);
        endDate.setDate(0);
        endDate.setHours(23, 59, 59, 999);
        return { start: startDate, end: endDate };
      }
      case 'last_month': {
        startDate.setMonth(today.getMonth() - 1);
        startDate.setDate(1);
        startDate.setHours(0, 0, 0, 0);
        endDate.setMonth(today.getMonth());
        endDate.setDate(0);
        endDate.setHours(23, 59, 59, 999);
        return { start: startDate, end: endDate };
      }
      default:
        return { start: null, end: null };
    }
  };

  const getDateFilterLabel = () => {
    const option = dateFilterOptions.find((opt) => opt.value === dateFilter);
    return option ? option.label : 'All Time';
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dateDropdownRef.current && !dateDropdownRef.current.contains(event.target)) {
        setShowDateDropdown(false);
      }
      if (guestSearchRef.current && !guestSearchRef.current.contains(event.target)) {
        setShowGuestSearch(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch guests
  const fetchGuests = async () => {
    try {
      setGuestSearchLoading(true);
      const response = await fetch(`${BQ_BASE_URL}/bq/api/guests/`, {
        method: 'GET',
        headers: { accept: 'application/json' },
      });
      const data = await response.json();
      if (data.success && data.guests) {
        setGuests(data.guests);
        setFilteredGuests(data.guests);
      }
    } catch (error) {
      console.error('Error fetching guests:', error);
      toast.error('Failed to load guest data');
    } finally {
      setGuestSearchLoading(false);
    }
  };

  // Handle guest search
  const handleGuestSearch = (searchTerm) => {
    setGuestSearchTerm(searchTerm);
    if (searchTerm.trim() === '') {
      setFilteredGuests(guests);
      setShowGuestSearch(false);
      return;
    }

    const filtered = guests.filter((guest) => {
      const fullName = `${guest.firstname} ${guest.lastname}`.toLowerCase();
      const phone = guest.phonenumber?.toString() || '';
      const email = guest.emailid?.toLowerCase() || '';
      const search = searchTerm.toLowerCase();

      return (
        fullName.includes(search) ||
        phone.includes(search) ||
        email.includes(search) ||
        guest.firstname?.toLowerCase().includes(search) ||
        guest.lastname?.toLowerCase().includes(search)
      );
    });

    setFilteredGuests(filtered);
    setShowGuestSearch(filtered.length > 0);
  };

  // Handle guest selection - Auto populate form
  const handleGuestSelect = (guest) => {
    setSelectedGuest(guest);
    setShowGuestSearch(false);
    setGuestSearchTerm(`${guest.firstname} ${guest.lastname}`);

    setWalkinFormData((prev) => ({
      ...prev,
      firstname: guest.firstname || '',
      lastname: guest.lastname || '',
      phonenumber: guest.phonenumber?.toString() || '',
      emailid: guest.emailid || '',
      countrycode: guest.countrycode || '+91',
      clienttype: guest.clienttype || 'Leisure',
      country: guest.country || 'India',
      companyid: guest.companyid || '',
      companyname: guest.companyname || '',
    }));

    // Clear errors for these fields
    setErrors((prev) => ({
      ...prev,
      firstname: '',
      lastname: '',
      phonenumber: '',
      emailid: '',
    }));

    toast.success(`Guest "${guest.firstname} ${guest.lastname}" loaded successfully!`);
  };

  // Check room availability using the check-dates API
  const checkAvailability = async () => {
    if (!walkinFormData.checkindate || !walkinFormData.checkoutdate) {
      toast.error('Please select check-in and check-out dates first');
      return;
    }

    // Validate dates
    const checkinDate = new Date(walkinFormData.checkindate);
    const checkoutDate = new Date(walkinFormData.checkoutdate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (checkinDate < today) {
      toast.error('Check-in date cannot be in the past');
      return;
    }

    if (checkoutDate <= checkinDate) {
      toast.error('Check-out date must be after check-in date');
      return;
    }

    setCheckingAvailability(true);
    setAvailabilityChecked(false);
    try {
      const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
      };

      const checkin = formatDate(walkinFormData.checkindate);
      const checkout = formatDate(walkinFormData.checkoutdate);

      const response = await fetch(
        `${BQ_BASE_URL}/bq/api/availability/check-dates/all?checkin=${checkin}&checkout=${checkout}`,
        {
          method: 'GET',
          headers: { accept: 'application/json' },
        }
      );

      const data = await response.json();

      if (response.ok) {
        setAvailabilityData(data.room_types || []);
        setShowAvailability(true);
        setAvailabilityChecked(true);
        toast.success('Availability loaded! Please select a room type.');

        // Clear room type selection if previously selected
        setWalkinFormData((prev) => ({
          ...prev,
          room_type: '',
        }));
      } else {
        toast.error(data.detail || 'Failed to check availability');
        setAvailabilityChecked(false);
      }
    } catch (error) {
      console.error('Error checking availability:', error);
      toast.error('Failed to check availability');
      setAvailabilityChecked(false);
    } finally {
      setCheckingAvailability(false);
    }
  };

  // Fetch room types
  const fetchRoomTypes = async () => {
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/roomtypes/`, {
        method: 'GET',
        headers: { accept: 'application/json' },
      });
      const data = await response.json();
      if (data && Array.isArray(data)) {
        setAvailableRoomTypes(data);
      }
    } catch (error) {
      console.error('Error fetching room types:', error);
    }
  };

  // Fetch companies
  const fetchCompanies = async () => {
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/companies`, {
        method: 'GET',
        headers: { accept: 'application/json' },
      });
      const data = await response.json();
      if (data && data.companies) {
        setCompanies(data.companies);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const handleOpenWalkinModal = () => {
    setShowWalkinModal(true);
    setWalkinResponse(null);
    fetchRoomTypes();
    fetchCompanies();
    fetchGuests();
    setCompanySearchTerm('');
    setFilteredCompanies([]);
    setShowCompanyDropdown(false);
    setShowAvailability(false);
    setAvailabilityData([]);
    setAvailabilityChecked(false);
    setShowGuestSearch(false);
    setGuestSearchTerm('');
    setSelectedGuest(null);
    setErrors({});
    setShowConfirmation(false);
  };

  // Validate phone number
  const validatePhoneNumber = (phone) => {
    const phoneStr = phone.toString();
    const phoneRegex = /^[+]?[0-9]{10,15}$/;
    return phoneRegex.test(phoneStr);
  };

  // Validate email
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Validate GST (optional)
  const validateGST = (gst) => {
    if (!gst) return true;
    const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[A-Z0-9]{1}Z[A-Z0-9]{1}$/;
    return gstRegex.test(gst);
  };

  // Validate form before showing confirmation
  const validateForm = () => {
    const newErrors = {};

    // Guest name validation
    if (!walkinFormData.firstname.trim()) {
      newErrors.firstname = 'First name is required';
    }
    if (!walkinFormData.lastname.trim()) {
      newErrors.lastname = 'Last name is required';
    }

    // Phone validation
    if (!walkinFormData.phonenumber) {
      newErrors.phonenumber = 'Phone number is required';
    } else if (!validatePhoneNumber(walkinFormData.phonenumber)) {
      newErrors.phonenumber = 'Please enter a valid phone number (10-15 digits)';
    }

    // Email validation
    if (!walkinFormData.emailid) {
      newErrors.emailid = 'Email address is required';
    } else if (!validateEmail(walkinFormData.emailid)) {
      newErrors.emailid = 'Please enter a valid email address';
    }

    // Date validation
    if (!walkinFormData.checkindate) {
      newErrors.checkindate = 'Check-in date is required';
    }
    if (!walkinFormData.checkoutdate) {
      newErrors.checkoutdate = 'Check-out date is required';
    }

    // Availability check validation
    if (!availabilityChecked) {
      newErrors.availability = 'Please check room availability first';
    }

    // Room type validation
    if (!walkinFormData.room_type) {
      newErrors.room_type = 'Please select a room type';
    }

    // Guest count validation based on max occupancy
    if (walkinFormData.room_type) {
      const selectedRoom = availableRoomTypes.find(
        (r) => r.roomtypename === walkinFormData.room_type
      );
      if (selectedRoom && walkinFormData.number_of_guests > selectedRoom.max_occupancy) {
        newErrors.number_of_guests = `Maximum ${selectedRoom.max_occupancy} guests allowed for this room type`;
      }
    }

    // Corporate validation
    if (walkinFormData.clienttype === 'Corporate') {
      if (!walkinFormData.companyid) {
        newErrors.company = 'Please select a company';
      }
      if (!walkinFormData.contractid) {
        newErrors.contractid = 'Contract ID is required';
      }
    }

    // GST validation (optional)
    if (walkinFormData.gst_number && !validateGST(walkinFormData.gst_number)) {
      newErrors.gst_number = 'Invalid GST number format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Calculate total price with discount and tax
  const calculatePriceDetails = () => {
    if (!walkinFormData.room_type) return null;

    const selectedRoom = availableRoomTypes.find(
      (r) => r.roomtypename === walkinFormData.room_type
    );
    if (!selectedRoom) return null;

    const basePrice = parseFloat(selectedRoom.baseprice) || 0;
    const nights = calculateNights();
    const subtotal = basePrice * nights;

    // Calculate discount
    let discountPercentage = 0;
    let discountAmount = 0;

    if (walkinFormData.clienttype === 'Corporate' && walkinFormData.contractid) {
      discountPercentage = getDiscountPercentage(walkinFormData.contractid);
      discountAmount = (subtotal * discountPercentage) / 100;
    }

    const afterDiscount = subtotal - discountAmount;
    const tax = afterDiscount * 0.05; // 5% tax on discounted amount
    const total = afterDiscount + tax;

    return {
      basePrice,
      nights,
      subtotal,
      discountPercentage,
      discountAmount,
      afterDiscount,
      tax,
      total,
      roomType: selectedRoom.roomtypename,
      maxOccupancy: selectedRoom.max_occupancy,
    };
  };

  const calculateNights = () => {
    if (!walkinFormData.checkindate || !walkinFormData.checkoutdate) return 0;
    const checkin = new Date(walkinFormData.checkindate);
    const checkout = new Date(walkinFormData.checkoutdate);
    const diffTime = Math.abs(checkout - checkin);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const handleWalkinInputChange = (e) => {
    const { name, value, type } = e.target;

    // Clear error for this field
    setErrors((prev) => ({
      ...prev,
      [name]: '',
    }));

    // If changing dates, reset availability check
    if (name === 'checkindate' || name === 'checkoutdate') {
      setShowAvailability(false);
      setAvailabilityData([]);
      setAvailabilityChecked(false);
      setWalkinFormData((prev) => ({
        ...prev,
        room_type: '',
      }));
    }

    setWalkinFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCompanySearch = (searchTerm) => {
    setCompanySearchTerm(searchTerm);
    if (searchTerm.trim() === '') {
      setFilteredCompanies([]);
      setShowCompanyDropdown(false);
      return;
    }
    const filtered = companies.filter(
      (company) =>
        company.companyname.toLowerCase().includes(searchTerm.toLowerCase()) ||
        company.companyid.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredCompanies(filtered);
    setShowCompanyDropdown(true);
  };

  const handleCompanySelect = (company) => {
    setWalkinFormData((prev) => ({
      ...prev,
      companyid: company.companyid,
      companyname: company.companyname,
      contractid: company.contractid || '',
    }));
    setCompanySearchTerm(company.companyname);
    setShowCompanyDropdown(false);
    setFilteredCompanies([]);
    setErrors((prev) => ({
      ...prev,
      company: '',
      contractid: '',
    }));
  };

  const clearAllFields = () => {
    setWalkinFormData({
      firstname: '',
      lastname: '',
      phonenumber: '',
      emailid: '',
      countrycode: '+91',
      clienttype: 'Leisure',
      companyid: '',
      companyname: '',
      contractid: '',
      country: 'India',
      checkindate: '',
      checkoutdate: '',
      room_type: '',
      number_of_guests: 1,
      quantity: 1,
      special_requests: '',
      gst_number: '',
    });
    setGuestSearchTerm('');
    setSelectedGuest(null);
    setShowGuestSearch(false);
    setCompanySearchTerm('');
    setFilteredCompanies([]);
    setShowCompanyDropdown(false);
    setShowAvailability(false);
    setAvailabilityData([]);
    setAvailabilityChecked(false);
    setErrors({});
    setShowConfirmation(false);
    toast.info('All fields cleared');
  };

  const getRoomAvailabilityFromAPI = (roomType) => {
    if (!availabilityData || availabilityData.length === 0) return null;
    return availabilityData.find((r) => r.room_type === roomType);
  };

  const formatToTwoDecimal = (amount) => {
    return parseFloat(amount || 0).toFixed(2);
  };

  // Handle "Proceed" from confirmation popup
  const handleProceedToCreate = () => {
    setShowConfirmation(false);
    handleWalkinSubmit();
  };

  // Handle "Edit" from confirmation popup
  const handleEditReservation = () => {
    setShowConfirmation(false);
  };

  // Handle "Create" button click - show confirmation first
  const handleCreateClick = () => {
    if (!validateForm()) {
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.querySelector(`[name="${firstErrorKey}"]`);
        if (errorElement) {
          errorElement.focus();
        }
      }
      return;
    }
    setShowConfirmation(true);
  };

  const handleWalkinSubmit = async () => {
    setWalkinLoading(true);
    try {
      const payload = {
        guest: {
          firstname: walkinFormData.firstname,
          lastname: walkinFormData.lastname,
          phonenumber: parseInt(walkinFormData.phonenumber),
          emailid: walkinFormData.emailid,
          countrycode: walkinFormData.countrycode,
          clienttype: walkinFormData.clienttype,
          companyid: walkinFormData.clienttype === 'Corporate' ? walkinFormData.companyid : '',
          companyname: walkinFormData.clienttype === 'Corporate' ? walkinFormData.companyname : '',
          contractid: walkinFormData.clienttype === 'Corporate' ? walkinFormData.contractid : '',
          country: walkinFormData.country,
          additional_guests: [],
        },
        booking: {
          checkindate: walkinFormData.checkindate,
          checkoutdate: walkinFormData.checkoutdate,
          room_type: walkinFormData.room_type,
          number_of_guests: parseInt(walkinFormData.number_of_guests),
          quantity: 1,
          special_requests: walkinFormData.special_requests || '',
          order_id: '',
          gst_number: walkinFormData.gst_number || '',
        },
      };

      const response = await fetch(`${BQ_BASE_URL}/bq/api/create-reservation-online-new/`, {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.detail) {
          if (Array.isArray(data.detail)) {
            const errorMessages = data.detail
              .map((err) => err.message || JSON.stringify(err))
              .join(', ');
            throw new Error(errorMessages);
          } else if (typeof data.detail === 'object' && data.detail.message) {
            throw new Error(data.detail.message);
          } else {
            throw new Error(data.detail || 'Failed to create reservation');
          }
        } else {
          throw new Error(data.message || 'Failed to create reservation');
        }
      }

      setWalkinResponse(data);
      setShowConfirmation(false);
      toast.success(
        <div>
          <strong>Reservation Created Successfully!</strong>
          <br />
          Booking ID: {data.booking_id}
        </div>,
        { autoClose: 5000 }
      );
    } catch (error) {
      console.error('Error creating reservation:', error);
      toast.error(error.message || 'Failed to create reservation');
    } finally {
      setWalkinLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowWalkinModal(false);
    setWalkinResponse(null);
    setWalkinFormData({
      firstname: '',
      lastname: '',
      phonenumber: '',
      emailid: '',
      countrycode: '+91',
      clienttype: 'Leisure',
      companyid: '',
      companyname: '',
      contractid: '',
      country: 'India',
      checkindate: '',
      checkoutdate: '',
      room_type: '',
      number_of_guests: 1,
      quantity: 1,
      special_requests: '',
      gst_number: '',
    });
    setCompanySearchTerm('');
    setFilteredCompanies([]);
    setShowCompanyDropdown(false);
    setShowGuestSearch(false);
    setGuestSearchTerm('');
    setSelectedGuest(null);
    setShowAvailability(false);
    setAvailabilityData([]);
    setAvailabilityChecked(false);
    setErrors({});
    setShowConfirmation(false);
    window.location.reload();
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        companyInputRef.current &&
        !companyInputRef.current.contains(event.target)
      ) {
        setShowCompanyDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get price details for display
  const priceDetails = calculatePriceDetails();

  return (
    <div className="p-6 min-h-screen bg-gray-100">
      {/* Header Section */}
      <header className="flex justify-between items-center mb-3 bg-white p-3">
        <h1 className="text-black text-xl font-bold ml-2">P M S</h1>
        <div className="flex space-x-6">
          <button
            onClick={() =>
              navigate('/dashboard/core-modules/property-management-system/front-desk-operations')
            }
            className="btn text-black font-bold"
          >
            Front Desk Operations
          </button>
          <button
            onClick={() =>
              navigate('/dashboard/core-modules/property-management-system/guest-managment')
            }
            className="btn "
          >
            Guest Management
          </button>
          <button
            onClick={() =>
              navigate('/dashboard/core-modules/property-management-system/front-desk-roomservices')
            }
            className="btn "
          >
            Hospitality Services
          </button>
          <button
            onClick={() =>
              navigate('/dashboard/core-modules/property-management-system/front-desk-amenities')
            }
            className="btn "
          >
            Facility Services
          </button>
          <button
            onClick={() =>
              navigate('/dashboard/core-modules/property-management-system/billing-payments')
            }
            className="btn  "
          >
            Billing & Payments
          </button>
          <button
            onClick={() =>
              navigate('/dashboard/core-modules/property-management-system/refunds-adjustments')
            }
            className="btn"
          >
            Refunds & Adjustments
          </button>
        </div>
      </header>

      {/* Title Section */}
      <div className="text-center mb-2">
        <div className="inline-block">
          <h1 className="text-2xl font-bold text-center text-black">Room Reservations</h1>
          <h1 className="text-sm font-poppins font-semibold text-center mb-1 text-gray-600">
            The room reservations displays guest details, reservation information, and facilitates
            key handover for efficient room management.
          </h1>
          <hr className="border-gray-400 mt-1 w-96 mx-auto" />
        </div>
      </div>

      {/* Search and Filter Section */}
      <div className="flex space-x-3 text-sm w-full mb-4">
        <div className="relative flex-1">
          <input
            type="text"
            placeholder="Search by last name"
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md shadow-sm placeholder:text-gray-500"
            value={lastNameQuery}
            onChange={(e) => setLastNameQuery(e.target.value)}
          />
          <FaSearch className="absolute top-3 left-3 transform text-gray-500" />
        </div>
        <div className="relative flex-1">
          <input
            type="text"
            placeholder="Search by reservation number"
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md shadow-sm placeholder:text-gray-500"
            value={bookingIdQuery}
            onChange={(e) => setBookingIdQuery(e.target.value)}
          />
          <FaSearch className="absolute top-3 left-3 transform text-gray-500" />
        </div>

        <div className="relative" ref={dateDropdownRef}>
          <button
            onClick={() => setShowDateDropdown(!showDateDropdown)}
            className="bg-white text-gray-700 px-4 py-2 h-9 border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 transition-colors flex items-center gap-2 whitespace-nowrap"
          >
            <FaCalendar className="h-3 w-3" />
            {getDateFilterLabel()}
            <FaChevronDown
              className={`h-3 w-3 transition-transform ${showDateDropdown ? 'rotate-180' : ''}`}
            />
          </button>
          {showDateDropdown && (
            <div className="absolute z-10 mt-1 w-48 bg-white border border-gray-200 rounded-md shadow-lg py-1">
              {dateFilterOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => {
                    setDateFilter(option.value);
                    setShowDateDropdown(false);
                  }}
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${
                    dateFilter === option.value
                      ? 'bg-gray-100 text-black font-medium'
                      : 'text-gray-700'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <button
          onClick={handleOpenWalkinModal}
          className="bg-black text-white px-3 py-0 h-9 font-semibold rounded-md shadow-md hover:bg-black transition-colors flex items-center gap-2"
        >
          <FaPlus className="h-2 w-2" />
          Create Reservation
        </button>

        <Link
          to={
            '/dashboard/core-modules/property-management-system/front-desk-operations/manage-rooms'
          }
        >
          <div className="mb-4">
            <button className="bg-black text-white px-7 py-2 font-semibold rounded-md shadow-md hover:text-white">
              Manage Rooms
            </button>
          </div>
        </Link>
        <Link
          to={
            '/dashboard/core-modules/property-management-system/front-desk-operations/damage-reports'
          }
        >
          <div className="mb-4">
            <button className="bg-black text-white px-5 py-2 font-semibold rounded-md shadow-md hover:text-white">
              Damage Reports
            </button>
          </div>
        </Link>
        <Link
          to={
            '/dashboard/core-modules/property-management-system/front-desk-operations/cancelled-booking'
          }
        >
          <div className="mb-4">
            <button className="bg-red-500 text-white px-5 py-2 font-semibold rounded-md shadow-md hover:text-white">
              Cancelled Bookings
            </button>
          </div>
        </Link>
      </div>

      {/* Table Section */}
      <ReservationTable
        lastNameQuery={lastNameQuery}
        bookingIdQuery={bookingIdQuery}
        dateFilter={dateFilter}
        dateRange={getDateRange(dateFilter)}
      />

      {/* Walk-in Reservation Modal */}
      {showWalkinModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl relative overflow-hidden shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            <button
              onClick={() => {
                setShowWalkinModal(false);
                setWalkinResponse(null);
                setWalkinFormData({
                  firstname: '',
                  lastname: '',
                  phonenumber: '',
                  emailid: '',
                  countrycode: '+91',
                  clienttype: 'Leisure',
                  companyid: '',
                  companyname: '',
                  contractid: '',
                  country: 'India',
                  checkindate: '',
                  checkoutdate: '',
                  room_type: '',
                  number_of_guests: 1,
                  quantity: 1,
                  special_requests: '',
                  gst_number: '',
                });
                setCompanySearchTerm('');
                setFilteredCompanies([]);
                setShowCompanyDropdown(false);
                setShowGuestSearch(false);
                setGuestSearchTerm('');
                setSelectedGuest(null);
                setShowAvailability(false);
                setAvailabilityData([]);
                setAvailabilityChecked(false);
                setErrors({});
                setShowConfirmation(false);
              }}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 p-2 rounded-full transition-colors z-10 hover:bg-gray-100"
            >
              <FiX className="h-5 w-5" />
            </button>

            <div className="p-6 overflow-y-auto flex-1">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-gray-100 p-2 rounded-full">
                  <FaUser className="h-6 w-6 text-gray-900" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Create Reservation</h2>
              </div>

              {!walkinResponse ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Left Column - Guest Information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-800 border-b pb-2 flex items-center gap-2">
                      <FaUser className="h-5 w-5" />
                      Guest Information
                    </h3>

                    <div className="relative" ref={guestSearchRef}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Existing Customer{' '}
                        <span className="text-xs text-gray-400">(search to auto-fill)</span>
                      </label>
                      <div className="relative">
                        <FaHistory className="absolute left-3 top-3 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Search by name, phone or email..."
                          value={guestSearchTerm}
                          onChange={(e) => handleGuestSearch(e.target.value)}
                          onFocus={() => {
                            if (guestSearchTerm.trim() !== '' && filteredGuests.length > 0) {
                              setShowGuestSearch(true);
                            }
                          }}
                          className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg"
                        />
                        {guestSearchLoading && (
                          <FaSpinner className="absolute right-3 top-3 text-gray-400 animate-spin" />
                        )}
                      </div>

                      {showGuestSearch && filteredGuests.length > 0 && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                          {filteredGuests.map((guest) => (
                            <div
                              key={guest.guestid}
                              onClick={() => handleGuestSelect(guest)}
                              className="px-4 py-3 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-0 flex items-start gap-3"
                            >
                              <div className="bg-blue-100 p-2 rounded-full flex-shrink-0">
                                <FaUserCheck className="h-4 w-4 text-blue-600" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-sm">
                                  {guest.firstname} {guest.lastname}
                                </div>
                                <div className="text-xs text-gray-500 flex flex-wrap gap-2">
                                  <span>📧 {guest.emailid}</span>
                                  <span>📱 {guest.phonenumber}</span>
                                  {guest.companyname && <span>🏢 {guest.companyname}</span>}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {selectedGuest && (
                        <div className="mt-1 text-xs text-green-600">
                          ✓ Guest loaded: {selectedGuest.firstname} {selectedGuest.lastname}
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          First Name *
                        </label>
                        <input
                          type="text"
                          name="firstname"
                          value={walkinFormData.firstname}
                          onChange={handleWalkinInputChange}
                          className={`w-full px-3 py-2 border rounded-lg ${
                            errors.firstname ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder="Enter first name"
                        />
                        {errors.firstname && (
                          <p className="text-red-500 text-xs mt-1">{errors.firstname}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Last Name *
                        </label>
                        <input
                          type="text"
                          name="lastname"
                          value={walkinFormData.lastname}
                          onChange={handleWalkinInputChange}
                          className={`w-full px-3 py-2 border rounded-lg ${
                            errors.lastname ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder="Enter last name"
                        />
                        {errors.lastname && (
                          <p className="text-red-500 text-xs mt-1">{errors.lastname}</p>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email Address *
                      </label>
                      <div className="flex items-center gap-2">
                        <FiMail className="text-gray-400" />
                        <input
                          type="email"
                          name="emailid"
                          value={walkinFormData.emailid}
                          onChange={handleWalkinInputChange}
                          className={`w-full px-3 py-2 border rounded-lg ${
                            errors.emailid ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder="Enter email address"
                        />
                      </div>
                      {errors.emailid && (
                        <p className="text-red-500 text-xs mt-1">{errors.emailid}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone Number *
                      </label>
                      <div className="flex gap-2">
                        <select
                          name="countrycode"
                          value={walkinFormData.countrycode}
                          onChange={handleWalkinInputChange}
                          className="w-24 px-3 py-2 border border-gray-300 rounded-lg"
                        >
                          <option value="+91">+91 (India)</option>
                          <option value="+1">+1 (USA)</option>
                          <option value="+44">+44 (UK)</option>
                          <option value="+61">+61 (Australia)</option>
                        </select>
                        <input
                          type="tel"
                          name="phonenumber"
                          value={walkinFormData.phonenumber}
                          onChange={handleWalkinInputChange}
                          className={`flex-1 px-3 py-2 border rounded-lg ${
                            errors.phonenumber ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder="Enter phone number"
                        />
                      </div>
                      {errors.phonenumber && (
                        <p className="text-red-500 text-xs mt-1">{errors.phonenumber}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Guest Type *
                      </label>
                      <select
                        name="clienttype"
                        value={walkinFormData.clienttype}
                        onChange={handleWalkinInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      >
                        <option value="Leisure">Leisure</option>
                        <option value="Corporate">Corporate</option>
                      </select>
                    </div>

                    {walkinFormData.clienttype === 'Corporate' && (
                      <>
                        <div className="flex items-center gap-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                          <FaBriefcase className="text-blue-600" />
                          <span className="text-sm font-medium text-blue-700">Corporate Guest</span>
                          {walkinFormData.companyname && (
                            <span className="text-sm text-gray-600 ml-1">
                              - {walkinFormData.companyname}
                            </span>
                          )}
                        </div>

                        <div className="relative">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Company *
                          </label>
                          <div className="relative" ref={companyInputRef}>
                            <FaBuilding className="text-gray-400 absolute left-3 top-3" />
                            <input
                              type="text"
                              value={companySearchTerm}
                              onChange={(e) => handleCompanySearch(e.target.value)}
                              onFocus={() => {
                                if (companySearchTerm.trim() !== '') {
                                  setShowCompanyDropdown(true);
                                }
                              }}
                              className={`w-full pl-10 pr-3 py-2 border rounded-lg ${
                                errors.company ? 'border-red-500' : 'border-gray-300'
                              }`}
                              placeholder="Search for company..."
                            />
                            {showCompanyDropdown && filteredCompanies.length > 0 && (
                              <div
                                ref={dropdownRef}
                                className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto"
                              >
                                {filteredCompanies.map((company) => (
                                  <div
                                    key={company.id}
                                    onClick={() => handleCompanySelect(company)}
                                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-0"
                                  >
                                    <div className="font-medium text-sm">{company.companyname}</div>
                                    <div className="text-xs text-gray-500">
                                      ID: {company.companyid}
                                      {company.contractid && ` | Contract: ${company.contractid}`}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                          {errors.company && (
                            <p className="text-red-500 text-xs mt-1">{errors.company}</p>
                          )}
                          {walkinFormData.companyid && (
                            <div className="mt-1 text-xs text-green-600">
                              ✓ Selected: {walkinFormData.companyname}
                            </div>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Contract ID/Discount code *
                          </label>
                          <div className="flex items-center gap-2">
                            <FaIdCard className="text-gray-400" />
                            <input
                              type="text"
                              name="contractid"
                              value={walkinFormData.contractid}
                              onChange={handleWalkinInputChange}
                              className={`w-full px-3 py-2 border rounded-lg ${
                                errors.contractid ? 'border-red-500' : 'border-gray-300'
                              }`}
                              placeholder="Enter contract ID (e.g., PECDIS_025)"
                            />
                          </div>
                          {errors.contractid && (
                            <p className="text-red-500 text-xs mt-1">{errors.contractid}</p>
                          )}
                          {walkinFormData.contractid && (
                            <p className="text-xs text-blue-600 mt-1">
                              {getDiscountPercentage(walkinFormData.contractid) > 0 ? (
                                <>
                                  💳 {getDiscountPercentage(walkinFormData.contractid)}% discount
                                  will be applied
                                </>
                              ) : (
                                <>⚠️ Invalid contract ID format. Use PECDIS_XXX format</>
                              )}
                            </p>
                          )}
                        </div>
                      </>
                    )}

                    {/* GST for Leisure */}
                    {walkinFormData.clienttype === 'Leisure' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          GST Number (Optional)
                        </label>
                        <input
                          type="text"
                          name="gst_number"
                          value={walkinFormData.gst_number}
                          onChange={handleWalkinInputChange}
                          className={`w-full px-3 py-2 border rounded-lg ${
                            errors.gst_number ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder="e.g., 22AAAAA0000A1Z5"
                        />
                        {errors.gst_number && (
                          <p className="text-red-500 text-xs mt-1">{errors.gst_number}</p>
                        )}
                        <p className="text-xs text-gray-500 mt-1">
                          Format: 22AAAAA0000A1Z5 (2 digits, 5 letters, 4 digits, 1 letter, 1
                          alphanumeric, Z, 1 alphanumeric)
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Right Column - Booking Details */}
                  <div className="space-y-4">
                    <div className="flex justify-between items-center border-b pb-2">
                      <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                        <FaCalendar className="h-5 w-5" />
                        Booking Details
                      </h3>
                      <button
                        onClick={clearAllFields}
                        className="px-3 py-1 text-sm bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors flex items-center gap-1"
                      >
                        <FaTimes className="h-3 w-3" />
                        Clear All
                      </button>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Check-in Date *
                        </label>
                        <input
                          type="date"
                          name="checkindate"
                          value={walkinFormData.checkindate}
                          onChange={handleWalkinInputChange}
                          className={`w-full px-3 py-2 border rounded-lg ${
                            errors.checkindate ? 'border-red-500' : 'border-gray-300'
                          }`}
                        />
                        {errors.checkindate && (
                          <p className="text-red-500 text-xs mt-1">{errors.checkindate}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Check-out Date *
                        </label>
                        <input
                          type="date"
                          name="checkoutdate"
                          value={walkinFormData.checkoutdate}
                          onChange={handleWalkinInputChange}
                          className={`w-full px-3 py-2 border rounded-lg ${
                            errors.checkoutdate ? 'border-red-500' : 'border-gray-300'
                          }`}
                        />
                        {errors.checkoutdate && (
                          <p className="text-red-500 text-xs mt-1">{errors.checkoutdate}</p>
                        )}
                      </div>
                    </div>

                    {/* Availability Check Button */}
                    <button
                      onClick={checkAvailability}
                      disabled={
                        checkingAvailability ||
                        !walkinFormData.checkindate ||
                        !walkinFormData.checkoutdate
                      }
                      className={`w-full py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                        checkingAvailability ||
                        !walkinFormData.checkindate ||
                        !walkinFormData.checkoutdate
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : availabilityChecked
                            ? 'bg-green-600 text-white hover:bg-green-700'
                            : 'bg-black text-white hover:bg-gray-800'
                      }`}
                    >
                      {checkingAvailability ? (
                        <>
                          <FaSpinner className="animate-spin" />
                          Checking Availability...
                        </>
                      ) : availabilityChecked ? (
                        <>
                          <FaCheckCircle className="h-4 w-4" />
                          Availability Checked ✓
                        </>
                      ) : (
                        <>
                          <FaSearch />
                          Check Room Availability
                        </>
                      )}
                    </button>
                    {errors.availability && (
                      <p className="text-red-500 text-xs">{errors.availability}</p>
                    )}

                    {/* Room Type Selection */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Room Type *
                      </label>
                      <select
                        name="room_type"
                        value={walkinFormData.room_type}
                        onChange={handleWalkinInputChange}
                        className={`w-full px-3 py-2 border rounded-lg ${
                          errors.room_type ? 'border-red-500' : 'border-gray-300'
                        }`}
                        disabled={!availabilityChecked}
                      >
                        <option value="">Select room type</option>
                        {availableRoomTypes.map((room) => {
                          const availability = getRoomAvailabilityFromAPI(room.roomtypename);
                          const isAvailable = availability
                            ? availability.available_rooms > 0
                            : true;
                          return (
                            <option
                              key={room.roomtypeid}
                              value={room.roomtypename}
                              disabled={!isAvailable}
                              className={!isAvailable ? 'text-gray-400' : ''}
                            >
                              {room.roomtypename}
                              {availability && ` (${availability.available_rooms} available)`}
                              {!availability && ' (Check availability)'}
                            </option>
                          );
                        })}
                      </select>
                      {errors.room_type && (
                        <p className="text-red-500 text-xs mt-1">{errors.room_type}</p>
                      )}
                      {walkinFormData.room_type && availabilityData.length > 0 && (
                        <p className="text-xs text-gray-500 mt-1">
                          {(() => {
                            const avail = getRoomAvailabilityFromAPI(walkinFormData.room_type);
                            return avail
                              ? `${avail.available_rooms} room(s) available for these dates`
                              : 'Check availability for these dates';
                          })()}
                        </p>
                      )}
                    </div>

                    {/* Price Display with Discount */}
                    {walkinFormData.room_type && priceDetails && (
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-700">
                            Room Price per night:
                            <span className="font-bold text-gray-900 ml-1">
                              ₹{priceDetails.basePrice}
                            </span>
                          </span>
                        </div>
                        <div className="text-sm text-gray-600">
                          {priceDetails.nights} night(s) × ₹{priceDetails.basePrice} =
                          <span className="font-medium ml-1">
                            ₹{formatToTwoDecimal(priceDetails.subtotal)}
                          </span>
                        </div>

                        {/* Show discount if applicable */}
                        {priceDetails.discountPercentage > 0 && (
                          <>
                            <div className="text-sm text-green-600 flex items-center gap-1">
                              Corporate Discount ({priceDetails.discountPercentage}% off): -₹
                              {formatToTwoDecimal(priceDetails.discountAmount)}
                            </div>
                            <div className="text-sm text-gray-600">
                              After Discount:
                              <span className="font-medium ml-1">
                                ₹{formatToTwoDecimal(priceDetails.afterDiscount)}
                              </span>
                            </div>
                          </>
                        )}

                        <div className="text-sm text-gray-600">
                          Tax (5%): ₹{formatToTwoDecimal(priceDetails.tax)}
                        </div>
                        <div className="text-sm font-bold text-gray-900 border-t pt-1 mt-1">
                          Total: ₹{formatToTwoDecimal(priceDetails.total)}
                        </div>
                        <div className="text-xs text-gray-500">
                          Max occupancy: {priceDetails.maxOccupancy} guests
                        </div>
                      </div>
                    )}

                    {/* Guest Count with max occupancy validation */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Number of Guests *
                      </label>
                      <input
                        type="number"
                        name="number_of_guests"
                        value={walkinFormData.number_of_guests}
                        onChange={handleWalkinInputChange}
                        min="1"
                        max={
                          walkinFormData.room_type
                            ? availableRoomTypes.find(
                                (r) => r.roomtypename === walkinFormData.room_type
                              )?.max_occupancy || 10
                            : 10
                        }
                        className={`w-full px-3 py-2 border rounded-lg ${
                          errors.number_of_guests ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {errors.number_of_guests && (
                        <p className="text-red-500 text-xs mt-1">{errors.number_of_guests}</p>
                      )}
                      {walkinFormData.room_type && (
                        <p className="text-xs text-gray-500 mt-1">
                          Max{' '}
                          {availableRoomTypes.find(
                            (r) => r.roomtypename === walkinFormData.room_type
                          )?.max_occupancy || 'N/A'}{' '}
                          guests allowed
                        </p>
                      )}
                    </div>

                    {/* Quantity - disabled and set to 1 */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Quantity (Rooms)
                      </label>
                      <input
                        type="number"
                        name="quantity"
                        value={1}
                        disabled
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Only 1 room allowed per reservation
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Special Requests
                      </label>
                      <textarea
                        name="special_requests"
                        value={walkinFormData.special_requests}
                        onChange={handleWalkinInputChange}
                        rows="2"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none"
                        placeholder="Any special requests or notes..."
                      />
                    </div>
                  </div>
                </div>
              ) : (
                // Success Response
                <div className="p-6 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <FaCheckCircle className="h-6 w-6 text-green-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <h4 className="font-semibold text-green-800 text-lg mb-3">
                        Reservation Created Successfully!
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div className="space-y-2">
                          <div>
                            <span className="text-gray-600">Reservation ID:</span>
                            <span className="ml-2 font-mono font-medium text-green-700">
                              {walkinResponse.order_id}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-600">Room Type:</span>
                            <span className="ml-2 text-green-700">{walkinResponse.room_type}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Guest Type:</span>
                            <span className="ml-2 text-green-700">{walkinResponse.guest_type}</span>
                          </div>
                          {walkinResponse.is_corporate && walkinResponse.company_name && (
                            <div>
                              <span className="text-gray-600">Company:</span>
                              <span className="ml-2 text-green-700">
                                {walkinResponse.company_name}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="space-y-2">
                          <div>
                            <span className="text-gray-600">Number Of Rooms:</span>
                            <span className="ml-2 text-green-700">{walkinResponse.quantity}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Final Amount:</span>
                            <span className="ml-2 font-medium text-green-700">
                              ₹{formatToTwoDecimal(walkinResponse.billing?.final_amount)} /
                              (Including Taxes & Fees)
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-600">Stay Duration:</span>
                            <span className="ml-2 text-green-700">
                              {walkinResponse.stay_details?.total_days || 0} nights
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="mt-4 pt-3 border-t border-green-200">
                        <p className="text-sm text-green-700 flex items-start gap-2">
                          <FaInfoCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                          Click "Close" to refresh and view the new reservation
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 bg-white border-t border-gray-200 flex justify-end gap-3">
              {!walkinResponse ? (
                <>
                  <button
                    onClick={() => {
                      setShowWalkinModal(false);
                      setWalkinResponse(null);
                      setWalkinFormData({
                        firstname: '',
                        lastname: '',
                        phonenumber: '',
                        emailid: '',
                        countrycode: '+91',
                        clienttype: 'Leisure',
                        companyid: '',
                        companyname: '',
                        contractid: '',
                        country: 'India',
                        checkindate: '',
                        checkoutdate: '',
                        room_type: '',
                        number_of_guests: 1,
                        quantity: 1,
                        special_requests: '',
                        gst_number: '',
                      });
                      setCompanySearchTerm('');
                      setFilteredCompanies([]);
                      setShowCompanyDropdown(false);
                      setShowGuestSearch(false);
                      setGuestSearchTerm('');
                      setSelectedGuest(null);
                      setShowAvailability(false);
                      setAvailabilityData([]);
                      setAvailabilityChecked(false);
                      setErrors({});
                      setShowConfirmation(false);
                    }}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateClick}
                    disabled={walkinLoading}
                    className={`px-6 py-2 rounded-lg font-medium text-white transition-colors flex items-center gap-2 ${
                      walkinLoading
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-black hover:bg-gray-800'
                    }`}
                  >
                    {walkinLoading ? (
                      <>
                        <FaSpinner className="animate-spin" />
                        Creating...
                      </>
                    ) : (
                      'Create Reservation'
                    )}
                  </button>
                </>
              ) : (
                <button
                  onClick={handleCloseModal}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors"
                >
                  Close
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Popup with Discount */}
      {showConfirmation && priceDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-yellow-100 p-2 rounded-full">
                  <FaExclamationTriangle className="h-6 w-6 text-yellow-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Confirm Reservation Details</h3>
              </div>

              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  {/* Guest */}
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-sm text-gray-600">Guest</p>
                    <p className="font-medium text-sm">
                      {walkinFormData.firstname} {walkinFormData.lastname}
                    </p>
                  </div>

                  {/* Stay Duration */}
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-sm text-gray-600">Stay Duration</p>
                    <p className="font-medium">{priceDetails.nights} nights</p>
                    <p className="text-xs text-gray-500">
                      {new Date(walkinFormData.checkindate).toLocaleDateString()} →{' '}
                      {new Date(walkinFormData.checkoutdate).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-sm text-gray-600">Room Type</p>
                    <p className="font-medium">{walkinFormData.room_type}</p>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-sm text-gray-600">Guest Count</p>
                    <p className="font-medium">{walkinFormData.number_of_guests} guest(s)</p>
                  </div>
                </div>

                {walkinFormData.clienttype === 'Corporate' && walkinFormData.companyname && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-sm text-gray-600">Company</p>
                    <p className="font-medium">{walkinFormData.companyname}</p>
                    {walkinFormData.contractid && (
                      <>
                        <p className="text-xs text-gray-500">
                          Contract: {walkinFormData.contractid}
                        </p>
                        {getDiscountPercentage(walkinFormData.contractid) > 0 && (
                          <p className="text-xs text-green-600 font-medium">
                            {getDiscountPercentage(walkinFormData.contractid)}% discount applied
                          </p>
                        )}
                      </>
                    )}
                  </div>
                )}

                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-sm text-gray-600">Price Breakdown</p>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span>
                        Room Rate (₹{priceDetails.basePrice} × {priceDetails.nights} nights)
                      </span>
                      <span>₹{formatToTwoDecimal(priceDetails.subtotal)}</span>
                    </div>

                    {/* Show discount in confirmation */}
                    {priceDetails.discountPercentage > 0 && (
                      <div className="flex justify-between text-green-600">
                        <span>Corporate Discount ({priceDetails.discountPercentage}%)</span>
                        <span>-₹{formatToTwoDecimal(priceDetails.discountAmount)}</span>
                      </div>
                    )}

                    {priceDetails.discountPercentage > 0 && (
                      <div className="flex justify-between text-gray-600">
                        <span>After Discount</span>
                        <span>₹{formatToTwoDecimal(priceDetails.afterDiscount)}</span>
                      </div>
                    )}

                    <div className="flex justify-between text-gray-600">
                      <span>Tax (5%)</span>
                      <span>₹{formatToTwoDecimal(priceDetails.tax)}</span>
                    </div>

                    <div className="flex justify-between font-bold text-lg border-t pt-1">
                      <span>Total</span>
                      <span>₹{formatToTwoDecimal(priceDetails.total)}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={handleEditReservation}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                >
                  Edit
                </button>
                <button
                  onClick={handleProceedToCreate}
                  disabled={walkinLoading}
                  className="flex-1 px-4 py-2 bg-black text-white rounded-lg font-medium hover:bg-gray-800 transition-colors flex items-center justify-center gap-2"
                >
                  {walkinLoading ? (
                    <>
                      <FaSpinner className="animate-spin" />
                      Creating...
                    </>
                  ) : (
                    'Proceed'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ToastContainer autoClose={3000} hideProgressBar className="mt-12" />
    </div>
  );
};

export default ReservationSearch;

// import React, { useState, useEffect, useRef } from 'react';
// import {
//   FaSearch,
//   FaPlus,
//   FaUser,
//   FaCalendar,
//   FaTimes,
//   FaBuilding,
//   FaIdCard,
//   FaChevronDown,
//   FaHistory,
//   FaUserCheck,
//   FaSpinner,
//   FaCheckCircle,
//   FaInfoCircle,
//   FaBriefcase,
//   FaRupeeSign,
// } from 'react-icons/fa';
// import ReservationTable from './ReservationTable';
// import { Link, useNavigate } from 'react-router-dom';
// import 'react-calendar/dist/Calendar.css';
// import { toast, ToastContainer } from 'react-toastify';
// import { FiX, FiMail, FiPhone } from 'react-icons/fi';

// const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

// const ReservationSearch = () => {
//   const [lastNameQuery, setLastNameQuery] = useState('');
//   const [bookingIdQuery, setBookingIdQuery] = useState('');
//   const [dateFilter, setDateFilter] = useState('all');
//   const [showDateDropdown, setShowDateDropdown] = useState(false);
//   const [showWalkinModal, setShowWalkinModal] = useState(false);
//   const [walkinLoading, setWalkinLoading] = useState(false);
//   const [walkinResponse, setWalkinResponse] = useState(null);
//   const [availableRoomTypes, setAvailableRoomTypes] = useState([]);
//   const [companies, setCompanies] = useState([]);
//   const [filteredCompanies, setFilteredCompanies] = useState([]);
//   const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
//   const [companySearchTerm, setCompanySearchTerm] = useState('');
//   const companyInputRef = useRef(null);
//   const dropdownRef = useRef(null);
//   const dateDropdownRef = useRef(null);

//   // Guest Search States
//   const [showGuestSearch, setShowGuestSearch] = useState(false);
//   const [guestSearchTerm, setGuestSearchTerm] = useState('');
//   const [guests, setGuests] = useState([]);
//   const [filteredGuests, setFilteredGuests] = useState([]);
//   const [guestSearchLoading, setGuestSearchLoading] = useState(false);
//   const [selectedGuest, setSelectedGuest] = useState(null);
//   const guestSearchRef = useRef(null);

//   // Availability States
//   const [checkingAvailability, setCheckingAvailability] = useState(false);
//   const [availabilityData, setAvailabilityData] = useState([]);
//   const [showAvailability, setShowAvailability] = useState(false);

//   const [walkinFormData, setWalkinFormData] = useState({
//     firstname: '',
//     lastname: '',
//     phonenumber: '',
//     emailid: '',
//     countrycode: '+91',
//     clienttype: 'Leisure',
//     companyid: '',
//     companyname: '',
//     contractid: '',
//     country: 'India',
//     checkindate: '',
//     checkoutdate: '',
//     room_type: '',
//     number_of_guests: 1,
//     quantity: 1,
//     special_requests: '',
//     gst_number: '',
//   });

//   const navigate = useNavigate();

//   // Date filter options
//   const dateFilterOptions = [
//     { value: 'all', label: 'All Time' },
//     { value: 'today', label: 'Today' },
//     { value: 'yesterday', label: 'Yesterday' },
//     { value: 'this_week', label: 'This Week' },
//     { value: 'last_week', label: 'Last Week' },
//     { value: 'this_month', label: 'This Month' },
//     { value: 'last_month', label: 'Last Month' },
//   ];

//   const getDateRange = (filter) => {
//     const today = new Date();
//     today.setHours(0, 0, 0, 0);
//     const startDate = new Date(today);
//     const endDate = new Date(today);

//     switch (filter) {
//       case 'today':
//         return { start: today, end: new Date(today.getTime() + 86400000 - 1) };
//       case 'yesterday':
//         startDate.setDate(startDate.getDate() - 1);
//         endDate.setDate(endDate.getDate() - 1);
//         endDate.setHours(23, 59, 59, 999);
//         return { start: startDate, end: endDate };
//       case 'this_week': {
//         const day = today.getDay();
//         const diff = today.getDate() - day + (day === 0 ? -6 : 1);
//         startDate.setDate(diff);
//         startDate.setHours(0, 0, 0, 0);
//         endDate.setDate(startDate.getDate() + 6);
//         endDate.setHours(23, 59, 59, 999);
//         return { start: startDate, end: endDate };
//       }
//       case 'last_week': {
//         const day = today.getDay();
//         const diff = today.getDate() - day + (day === 0 ? -6 : 1) - 7;
//         startDate.setDate(diff);
//         startDate.setHours(0, 0, 0, 0);
//         endDate.setDate(startDate.getDate() + 6);
//         endDate.setHours(23, 59, 59, 999);
//         return { start: startDate, end: endDate };
//       }
//       case 'this_month': {
//         startDate.setDate(1);
//         startDate.setHours(0, 0, 0, 0);
//         endDate.setMonth(today.getMonth() + 1);
//         endDate.setDate(0);
//         endDate.setHours(23, 59, 59, 999);
//         return { start: startDate, end: endDate };
//       }
//       case 'last_month': {
//         startDate.setMonth(today.getMonth() - 1);
//         startDate.setDate(1);
//         startDate.setHours(0, 0, 0, 0);
//         endDate.setMonth(today.getMonth());
//         endDate.setDate(0);
//         endDate.setHours(23, 59, 59, 999);
//         return { start: startDate, end: endDate };
//       }
//       default:
//         return { start: null, end: null };
//     }
//   };

//   const getDateFilterLabel = () => {
//     const option = dateFilterOptions.find((opt) => opt.value === dateFilter);
//     return option ? option.label : 'All Time';
//   };

//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (dateDropdownRef.current && !dateDropdownRef.current.contains(event.target)) {
//         setShowDateDropdown(false);
//       }
//       if (guestSearchRef.current && !guestSearchRef.current.contains(event.target)) {
//         setShowGuestSearch(false);
//       }
//     };
//     document.addEventListener('mousedown', handleClickOutside);
//     return () => document.removeEventListener('mousedown', handleClickOutside);
//   }, []);

//   // Fetch guests
//   const fetchGuests = async () => {
//     try {
//       setGuestSearchLoading(true);
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/guests/?skip=0&limit=100`, {
//         method: 'GET',
//         headers: { accept: 'application/json' },
//       });
//       const data = await response.json();
//       if (data.success && data.guests) {
//         setGuests(data.guests);
//         setFilteredGuests(data.guests);
//       }
//     } catch (error) {
//       console.error('Error fetching guests:', error);
//       toast.error('Failed to load guest data');
//     } finally {
//       setGuestSearchLoading(false);
//     }
//   };

//   // Handle guest search
//   const handleGuestSearch = (searchTerm) => {
//     setGuestSearchTerm(searchTerm);
//     if (searchTerm.trim() === '') {
//       setFilteredGuests(guests);
//       setShowGuestSearch(false);
//       return;
//     }

//     const filtered = guests.filter((guest) => {
//       const fullName = `${guest.firstname} ${guest.lastname}`.toLowerCase();
//       const phone = guest.phonenumber?.toString() || '';
//       const email = guest.emailid?.toLowerCase() || '';
//       const search = searchTerm.toLowerCase();

//       return (
//         fullName.includes(search) ||
//         phone.includes(search) ||
//         email.includes(search) ||
//         guest.firstname?.toLowerCase().includes(search) ||
//         guest.lastname?.toLowerCase().includes(search)
//       );
//     });

//     setFilteredGuests(filtered);
//     setShowGuestSearch(filtered.length > 0);
//   };

//   // Handle guest selection - Auto populate form
//   const handleGuestSelect = (guest) => {
//     setSelectedGuest(guest);
//     setShowGuestSearch(false);
//     setGuestSearchTerm(`${guest.firstname} ${guest.lastname}`);

//     setWalkinFormData((prev) => ({
//       ...prev,
//       firstname: guest.firstname || '',
//       lastname: guest.lastname || '',
//       phonenumber: guest.phonenumber?.toString() || '',
//       emailid: guest.emailid || '',
//       countrycode: guest.countrycode || '+91',
//       clienttype: guest.clienttype || 'Leisure',
//       country: guest.country || 'India',
//       companyid: guest.companyid || '',
//       companyname: guest.companyname || '',
//     }));

//     toast.success(`Guest "${guest.firstname} ${guest.lastname}" loaded successfully!`);
//   };

//   // Check room availability using the check-dates API
//   const checkAvailability = async () => {
//     if (!walkinFormData.checkindate || !walkinFormData.checkoutdate) {
//       toast.error('Please select check-in and check-out dates first');
//       return;
//     }

//     setCheckingAvailability(true);
//     try {
//       const formatDate = (dateStr) => {
//         const date = new Date(dateStr);
//         const day = String(date.getDate()).padStart(2, '0');
//         const month = String(date.getMonth() + 1).padStart(2, '0');
//         const year = date.getFullYear();
//         return `${day}-${month}-${year}`;
//       };

//       const checkin = formatDate(walkinFormData.checkindate);
//       const checkout = formatDate(walkinFormData.checkoutdate);

//       const response = await fetch(
//         `${BQ_BASE_URL}/bq/api/availability/check-dates/all?checkin=${checkin}&checkout=${checkout}`,
//         {
//           method: 'GET',
//           headers: { accept: 'application/json' },
//         }
//       );

//       const data = await response.json();

//       if (response.ok) {
//         setAvailabilityData(data.room_types || []);
//         setShowAvailability(true);
//         toast.success('Availability loaded!');
//       } else {
//         toast.error(data.detail || 'Failed to check availability');
//       }
//     } catch (error) {
//       console.error('Error checking availability:', error);
//       toast.error('Failed to check availability');
//     } finally {
//       setCheckingAvailability(false);
//     }
//   };

//   // Fetch room types
//   const fetchRoomTypes = async () => {
//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/roomtypes/`, {
//         method: 'GET',
//         headers: { accept: 'application/json' },
//       });
//       const data = await response.json();
//       if (data && Array.isArray(data)) {
//         setAvailableRoomTypes(data);
//       }
//     } catch (error) {
//       console.error('Error fetching room types:', error);
//     }
//   };

//   // Fetch companies
//   const fetchCompanies = async () => {
//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/companies`, {
//         method: 'GET',
//         headers: { accept: 'application/json' },
//       });
//       const data = await response.json();
//       if (data && data.companies) {
//         setCompanies(data.companies);
//       }
//     } catch (error) {
//       console.error('Error fetching companies:', error);
//     }
//   };

//   const handleOpenWalkinModal = () => {
//     setShowWalkinModal(true);
//     setWalkinResponse(null);
//     fetchRoomTypes();
//     fetchCompanies();
//     fetchGuests();
//     setCompanySearchTerm('');
//     setFilteredCompanies([]);
//     setShowCompanyDropdown(false);
//     setShowAvailability(false);
//     setAvailabilityData([]);
//     setShowGuestSearch(false);
//     setGuestSearchTerm('');
//     setSelectedGuest(null);
//   };

//   const handleWalkinInputChange = (e) => {
//     const { name, value } = e.target;
//     setWalkinFormData((prev) => ({
//       ...prev,
//       [name]: value,
//     }));

//     if (name === 'checkindate' || name === 'checkoutdate') {
//       setShowAvailability(false);
//       setAvailabilityData([]);
//     }
//   };

//   const handleCompanySearch = (searchTerm) => {
//     setCompanySearchTerm(searchTerm);
//     if (searchTerm.trim() === '') {
//       setFilteredCompanies([]);
//       setShowCompanyDropdown(false);
//       return;
//     }
//     const filtered = companies.filter(
//       (company) =>
//         company.companyname.toLowerCase().includes(searchTerm.toLowerCase()) ||
//         company.companyid.toLowerCase().includes(searchTerm.toLowerCase())
//     );
//     setFilteredCompanies(filtered);
//     setShowCompanyDropdown(true);
//   };

//   const handleCompanySelect = (company) => {
//     setWalkinFormData((prev) => ({
//       ...prev,
//       companyid: company.companyid,
//       companyname: company.companyname,
//       contractid: company.contractid || '',
//     }));
//     setCompanySearchTerm(company.companyname);
//     setShowCompanyDropdown(false);
//     setFilteredCompanies([]);
//   };

//   const clearAllFields = () => {
//     setWalkinFormData({
//       firstname: '',
//       lastname: '',
//       phonenumber: '',
//       emailid: '',
//       countrycode: '+91',
//       clienttype: 'Leisure',
//       companyid: '',
//       companyname: '',
//       contractid: '',
//       country: 'India',
//       checkindate: '',
//       checkoutdate: '',
//       room_type: '',
//       number_of_guests: 1,
//       quantity: 1,
//       special_requests: '',
//       gst_number: '',
//     });
//     setGuestSearchTerm('');
//     setSelectedGuest(null);
//     setShowGuestSearch(false);
//     setCompanySearchTerm('');
//     setFilteredCompanies([]);
//     setShowCompanyDropdown(false);
//     setShowAvailability(false);
//     setAvailabilityData([]);
//     toast.info('All fields cleared');
//   };

//   const getRoomAvailabilityFromAPI = (roomType) => {
//     if (!availabilityData || availabilityData.length === 0) return null;
//     return availabilityData.find((r) => r.room_type === roomType);
//   };

//   const formatToTwoDecimal = (amount) => {
//     return parseFloat(amount || 0).toFixed(2);
//   };

//   const handleWalkinSubmit = async () => {
//     if (!walkinFormData.firstname || !walkinFormData.lastname) {
//       toast.error('Please enter guest name');
//       return;
//     }
//     if (!walkinFormData.phonenumber) {
//       toast.error('Please enter phone number');
//       return;
//     }
//     if (!walkinFormData.emailid) {
//       toast.error('Email address is required');
//       return;
//     }
//     if (!walkinFormData.checkindate || !walkinFormData.checkoutdate) {
//       toast.error('Please select check-in and check-out dates');
//       return;
//     }
//     if (!walkinFormData.room_type) {
//       toast.error('Please select room type');
//       return;
//     }

//     if (walkinFormData.clienttype === 'Corporate') {
//       if (!walkinFormData.companyid) {
//         toast.error('Please select a company for corporate booking');
//         return;
//       }
//       if (!walkinFormData.contractid) {
//         toast.error('Contract ID is required for corporate booking');
//         return;
//       }
//     }

//     setWalkinLoading(true);
//     try {
//       const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
//       if (!emailRegex.test(walkinFormData.emailid)) {
//         toast.error('Please enter a valid email address');
//         setWalkinLoading(false);
//         return;
//       }

//       const payload = {
//         guest: {
//           firstname: walkinFormData.firstname,
//           lastname: walkinFormData.lastname,
//           phonenumber: parseInt(walkinFormData.phonenumber),
//           emailid: walkinFormData.emailid,
//           countrycode: walkinFormData.countrycode,
//           clienttype: walkinFormData.clienttype,
//           companyid: walkinFormData.clienttype === 'Corporate' ? walkinFormData.companyid : '',
//           companyname: walkinFormData.clienttype === 'Corporate' ? walkinFormData.companyname : '',
//           contractid: walkinFormData.clienttype === 'Corporate' ? walkinFormData.contractid : '',
//           country: walkinFormData.country,
//           additional_guests: [],
//         },
//         booking: {
//           checkindate: walkinFormData.checkindate,
//           checkoutdate: walkinFormData.checkoutdate,
//           room_type: walkinFormData.room_type,
//           number_of_guests: parseInt(walkinFormData.number_of_guests),
//           quantity: parseInt(walkinFormData.quantity),
//           special_requests: walkinFormData.special_requests || '',
//           // ✅ Senior discount completely removed
//           order_id: '',
//           gst_number: walkinFormData.gst_number || '',
//         },
//       };

//       const response = await fetch(`${BQ_BASE_URL}/bq/api/create-reservation-online-new/`, {
//         method: 'POST',
//         headers: {
//           accept: 'application/json',
//           'Content-Type': 'application/json',
//         },
//         body: JSON.stringify(payload),
//       });

//       const data = await response.json();

//       if (!response.ok) {
//         if (data.detail) {
//           if (Array.isArray(data.detail)) {
//             const errorMessages = data.detail
//               .map((err) => err.message || JSON.stringify(err))
//               .join(', ');
//             throw new Error(errorMessages);
//           } else if (typeof data.detail === 'object' && data.detail.message) {
//             throw new Error(data.detail.message);
//           } else {
//             throw new Error(data.detail || 'Failed to create reservation');
//           }
//         } else {
//           throw new Error(data.message || 'Failed to create reservation');
//         }
//       }

//       setWalkinResponse(data);
//       toast.success(
//         <div>
//           <strong>Reservation Created Successfully!</strong>
//           <br />
//           Booking ID: {data.booking_id}
//         </div>,
//         { autoClose: 5000 }
//       );
//     } catch (error) {
//       console.error('Error creating reservation:', error);
//       toast.error(error.message || 'Failed to create reservation');
//     } finally {
//       setWalkinLoading(false);
//     }
//   };

//   // ✅ Handle close - refresh only when user clicks close
//   const handleCloseModal = () => {
//     setShowWalkinModal(false);
//     setWalkinResponse(null);
//     setWalkinFormData({
//       firstname: '',
//       lastname: '',
//       phonenumber: '',
//       emailid: '',
//       countrycode: '+91',
//       clienttype: 'Leisure',
//       companyid: '',
//       companyname: '',
//       contractid: '',
//       country: 'India',
//       checkindate: '',
//       checkoutdate: '',
//       room_type: '',
//       number_of_guests: 1,
//       quantity: 1,
//       special_requests: '',
//       gst_number: '',
//     });
//     setCompanySearchTerm('');
//     setFilteredCompanies([]);
//     setShowCompanyDropdown(false);
//     setShowGuestSearch(false);
//     setGuestSearchTerm('');
//     setSelectedGuest(null);
//     setShowAvailability(false);
//     setAvailabilityData([]);
//     // ✅ Refresh the page when user explicitly closes the modal
//     window.location.reload();
//   };

//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (
//         dropdownRef.current &&
//         !dropdownRef.current.contains(event.target) &&
//         companyInputRef.current &&
//         !companyInputRef.current.contains(event.target)
//       ) {
//         setShowCompanyDropdown(false);
//       }
//     };
//     document.addEventListener('mousedown', handleClickOutside);
//     return () => document.removeEventListener('mousedown', handleClickOutside);
//   }, []);

//   return (
//     <div className="p-6 min-h-screen bg-gray-100">
//       {/* Header Section */}
//       <header className="flex justify-between items-center mb-3 bg-white p-3">
//         <h1 className="text-black text-xl font-bold ml-2">P M S</h1>
//         <div className="flex space-x-6">
//           <button
//             onClick={() =>
//               navigate('/dashboard/core-modules/property-management-system/front-desk-operations')
//             }
//             className="btn text-black font-bold"
//           >
//             Front Desk Operations
//           </button>
//           <button
//             onClick={() =>
//               navigate('/dashboard/core-modules/property-management-system/guest-managment')
//             }
//             className="btn "
//           >
//             Guest Management
//           </button>
//           <button
//             onClick={() =>
//               navigate('/dashboard/core-modules/property-management-system/front-desk-roomservices')
//             }
//             className="btn "
//           >
//             Hospitality Services
//           </button>
//           <button
//             onClick={() =>
//               navigate('/dashboard/core-modules/property-management-system/front-desk-amenities')
//             }
//             className="btn "
//           >
//             Facility Services
//           </button>
//           <button
//             onClick={() =>
//               navigate('/dashboard/core-modules/property-management-system/billing-payments')
//             }
//             className="btn  "
//           >
//             Billing & Payments
//           </button>
//           <button
//             onClick={() =>
//               navigate('/dashboard/core-modules/property-management-system/refunds-adjustments')
//             }
//             className="btn"
//           >
//             Refunds & Adjustments
//           </button>
//         </div>
//       </header>

//       {/* Title Section */}
//       <div className="text-center mb-2">
//         <div className="inline-block">
//           <h1 className="text-2xl font-bold text-center text-black">Room Reservations</h1>
//           <h1 className="text-sm font-poppins font-semibold text-center mb-1 text-gray-600">
//             The room reservations displays guest details, reservation information, and facilitates
//             key handover for efficient room management.
//           </h1>
//           <hr className="border-gray-400 mt-1 w-96 mx-auto" />
//         </div>
//       </div>

//       {/* Search and Filter Section */}
//       <div className="flex space-x-3 text-sm w-full mb-4">
//         <div className="relative flex-1">
//           <input
//             type="text"
//             placeholder="Search by last name"
//             className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md shadow-sm placeholder:text-gray-500"
//             value={lastNameQuery}
//             onChange={(e) => setLastNameQuery(e.target.value)}
//           />
//           <FaSearch className="absolute top-3 left-3 transform text-gray-500" />
//         </div>
//         <div className="relative flex-1">
//           <input
//             type="text"
//             placeholder="Search by reservation number"
//             className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md shadow-sm placeholder:text-gray-500"
//             value={bookingIdQuery}
//             onChange={(e) => setBookingIdQuery(e.target.value)}
//           />
//           <FaSearch className="absolute top-3 left-3 transform text-gray-500" />
//         </div>

//         <div className="relative" ref={dateDropdownRef}>
//           <button
//             onClick={() => setShowDateDropdown(!showDateDropdown)}
//             className="bg-white text-gray-700 px-4 py-2 h-9 border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 transition-colors flex items-center gap-2 whitespace-nowrap"
//           >
//             <FaCalendar className="h-3 w-3" />
//             {getDateFilterLabel()}
//             <FaChevronDown
//               className={`h-3 w-3 transition-transform ${showDateDropdown ? 'rotate-180' : ''}`}
//             />
//           </button>
//           {showDateDropdown && (
//             <div className="absolute z-10 mt-1 w-48 bg-white border border-gray-200 rounded-md shadow-lg py-1">
//               {dateFilterOptions.map((option) => (
//                 <button
//                   key={option.value}
//                   onClick={() => {
//                     setDateFilter(option.value);
//                     setShowDateDropdown(false);
//                   }}
//                   className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${
//                     dateFilter === option.value
//                       ? 'bg-gray-100 text-black font-medium'
//                       : 'text-gray-700'
//                   }`}
//                 >
//                   {option.label}
//                 </button>
//               ))}
//             </div>
//           )}
//         </div>

//         <button
//           onClick={handleOpenWalkinModal}
//           className="bg-black text-white px-3 py-0 h-9 font-semibold rounded-md shadow-md hover:bg-black transition-colors flex items-center gap-2"
//         >
//           <FaPlus className="h-2 w-2" />
//           Create Reservation
//         </button>

//         <Link
//           to={
//             '/dashboard/core-modules/property-management-system/front-desk-operations/manage-rooms'
//           }
//         >
//           <div className="mb-4">
//             <button className="bg-black text-white px-7 py-2 font-semibold rounded-md shadow-md hover:text-white">
//               Manage Rooms
//             </button>
//           </div>
//         </Link>
//         <Link
//           to={
//             '/dashboard/core-modules/property-management-system/front-desk-operations/damage-reports'
//           }
//         >
//           <div className="mb-4">
//             <button className="bg-black text-white px-5 py-2 font-semibold rounded-md shadow-md hover:text-white">
//               Damage Reports
//             </button>
//           </div>
//         </Link>
//         <Link
//           to={
//             '/dashboard/core-modules/property-management-system/front-desk-operations/cancelled-booking'
//           }
//         >
//           <div className="mb-4">
//             <button className="bg-red-500 text-white px-5 py-2 font-semibold rounded-md shadow-md hover:text-white">
//               Cancelled Bookings
//             </button>
//           </div>
//         </Link>
//       </div>

//       {/* Table Section */}
//       <ReservationTable
//         lastNameQuery={lastNameQuery}
//         bookingIdQuery={bookingIdQuery}
//         dateFilter={dateFilter}
//         dateRange={getDateRange(dateFilter)}
//       />

//       {/* Walk-in Reservation Modal */}
//       {showWalkinModal && (
//         <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-50">
//           <div className="bg-white rounded-2xl relative overflow-hidden shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
//             <button
//               onClick={() => {
//                 setShowWalkinModal(false);
//                 setWalkinResponse(null);
//                 setWalkinFormData({
//                   firstname: '',
//                   lastname: '',
//                   phonenumber: '',
//                   emailid: '',
//                   countrycode: '+91',
//                   clienttype: 'Leisure',
//                   companyid: '',
//                   companyname: '',
//                   contractid: '',
//                   country: 'India',
//                   checkindate: '',
//                   checkoutdate: '',
//                   room_type: '',
//                   number_of_guests: 1,
//                   quantity: 1,
//                   special_requests: '',
//                   gst_number: '',
//                 });
//                 setCompanySearchTerm('');
//                 setFilteredCompanies([]);
//                 setShowCompanyDropdown(false);
//                 setShowGuestSearch(false);
//                 setGuestSearchTerm('');
//                 setSelectedGuest(null);
//                 setShowAvailability(false);
//                 setAvailabilityData([]);
//               }}
//               className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 p-2 rounded-full transition-colors z-10 hover:bg-gray-100"
//             >
//               <FiX className="h-5 w-5" />
//             </button>

//             <div className="p-6 overflow-y-auto flex-1">
//               <div className="flex items-center gap-3 mb-6">
//                 <div className="bg-gray-100 p-2 rounded-full">
//                   <FaUser className="h-6 w-6 text-gray-900" />
//                 </div>
//                 <h2 className="text-2xl font-bold text-gray-900">Create Reservation</h2>
//               </div>

//               {!walkinResponse ? (
//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                   {/* Left Column - Guest Information */}
//                   <div className="space-y-4">
//                     <h3 className="text-lg font-semibold text-gray-800 border-b pb-2 flex items-center gap-2">
//                       <FaUser className="h-5 w-5" />
//                       Guest Information
//                     </h3>

//                     <div className="relative" ref={guestSearchRef}>
//                       <label className="block text-sm font-medium text-gray-700 mb-1">
//                         Existing Customer{' '}
//                         <span className="text-xs text-gray-400">(search to auto-fill)</span>
//                       </label>
//                       <div className="relative">
//                         <FaHistory className="absolute left-3 top-3 text-gray-400" />
//                         <input
//                           type="text"
//                           placeholder="Search by name, phone or email..."
//                           value={guestSearchTerm}
//                           onChange={(e) => handleGuestSearch(e.target.value)}
//                           onFocus={() => {
//                             if (guestSearchTerm.trim() !== '' && filteredGuests.length > 0) {
//                               setShowGuestSearch(true);
//                             }
//                           }}
//                           className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg"
//                         />
//                         {guestSearchLoading && (
//                           <FaSpinner className="absolute right-3 top-3 text-gray-400 animate-spin" />
//                         )}
//                       </div>

//                       {showGuestSearch && filteredGuests.length > 0 && (
//                         <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
//                           {filteredGuests.map((guest) => (
//                             <div
//                               key={guest.guestid}
//                               onClick={() => handleGuestSelect(guest)}
//                               className="px-4 py-3 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-0 flex items-start gap-3"
//                             >
//                               <div className="bg-blue-100 p-2 rounded-full flex-shrink-0">
//                                 <FaUserCheck className="h-4 w-4 text-blue-600" />
//                               </div>
//                               <div className="flex-1 min-w-0">
//                                 <div className="font-medium text-sm">
//                                   {guest.firstname} {guest.lastname}
//                                 </div>
//                                 <div className="text-xs text-gray-500 flex flex-wrap gap-2">
//                                   <span>📧 {guest.emailid}</span>
//                                   <span>📱 {guest.phonenumber}</span>
//                                   {guest.companyname && <span>🏢 {guest.companyname}</span>}
//                                 </div>
//                               </div>
//                             </div>
//                           ))}
//                         </div>
//                       )}

//                       {selectedGuest && (
//                         <div className="mt-1 text-xs text-green-600">
//                           ✓ Guest loaded: {selectedGuest.firstname} {selectedGuest.lastname}
//                         </div>
//                       )}
//                     </div>

//                     <div className="grid grid-cols-2 gap-4">
//                       <div>
//                         <label className="block text-sm font-medium text-gray-700 mb-1">
//                           First Name *
//                         </label>
//                         <input
//                           type="text"
//                           name="firstname"
//                           value={walkinFormData.firstname}
//                           onChange={handleWalkinInputChange}
//                           className="w-full px-3 py-2 border border-gray-300 rounded-lg"
//                           placeholder="Enter first name"
//                         />
//                       </div>
//                       <div>
//                         <label className="block text-sm font-medium text-gray-700 mb-1">
//                           Last Name *
//                         </label>
//                         <input
//                           type="text"
//                           name="lastname"
//                           value={walkinFormData.lastname}
//                           onChange={handleWalkinInputChange}
//                           className="w-full px-3 py-2 border border-gray-300 rounded-lg"
//                           placeholder="Enter last name"
//                         />
//                       </div>
//                     </div>

//                     <div>
//                       <label className="block text-sm font-medium text-gray-700 mb-1">
//                         Email Address *
//                       </label>
//                       <div className="flex items-center gap-2">
//                         <FiMail className="text-gray-400" />
//                         <input
//                           type="email"
//                           name="emailid"
//                           value={walkinFormData.emailid}
//                           onChange={handleWalkinInputChange}
//                           className="w-full px-3 py-2 border border-gray-300 rounded-lg"
//                           placeholder="Enter email address"
//                         />
//                       </div>
//                     </div>

//                     <div>
//                       <label className="block text-sm font-medium text-gray-700 mb-1">
//                         Phone Number *
//                       </label>
//                       <div className="flex gap-2">
//                         <select
//                           name="countrycode"
//                           value={walkinFormData.countrycode}
//                           onChange={handleWalkinInputChange}
//                           className="w-24 px-3 py-2 border border-gray-300 rounded-lg"
//                         >
//                           <option value="+91">+91 (India)</option>
//                           <option value="+1">+1 (USA)</option>
//                           <option value="+44">+44 (UK)</option>
//                           <option value="+61">+61 (Australia)</option>
//                         </select>
//                         <input
//                           type="tel"
//                           name="phonenumber"
//                           value={walkinFormData.phonenumber}
//                           onChange={handleWalkinInputChange}
//                           className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
//                           placeholder="Enter phone number"
//                         />
//                       </div>
//                     </div>

//                     <div>
//                       <label className="block text-sm font-medium text-gray-700 mb-1">
//                         Guest Type *
//                       </label>
//                       <select
//                         name="clienttype"
//                         value={walkinFormData.clienttype}
//                         onChange={handleWalkinInputChange}
//                         className="w-full px-3 py-2 border border-gray-300 rounded-lg"
//                       >
//                         <option value="Leisure">Leisure</option>
//                         <option value="Corporate">Corporate</option>
//                       </select>
//                     </div>

//                     {walkinFormData.clienttype === 'Corporate' && (
//                       <>
//                         <div className="flex items-center gap-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
//                           <FaBriefcase className="text-blue-600" />
//                           <span className="text-sm font-medium text-blue-700">Corporate Guest</span>
//                           {walkinFormData.companyname && (
//                             <span className="text-sm text-gray-600 ml-1">
//                               - {walkinFormData.companyname}
//                             </span>
//                           )}
//                         </div>

//                         <div className="relative">
//                           <label className="block text-sm font-medium text-gray-700 mb-1">
//                             Company *
//                           </label>
//                           <div className="relative" ref={companyInputRef}>
//                             <FaBuilding className="text-gray-400 absolute left-3 top-3" />
//                             <input
//                               type="text"
//                               value={companySearchTerm}
//                               onChange={(e) => handleCompanySearch(e.target.value)}
//                               onFocus={() => {
//                                 if (companySearchTerm.trim() !== '') {
//                                   setShowCompanyDropdown(true);
//                                 }
//                               }}
//                               className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg"
//                               placeholder="Search for company..."
//                             />
//                             {showCompanyDropdown && filteredCompanies.length > 0 && (
//                               <div
//                                 ref={dropdownRef}
//                                 className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto"
//                               >
//                                 {filteredCompanies.map((company) => (
//                                   <div
//                                     key={company.id}
//                                     onClick={() => handleCompanySelect(company)}
//                                     className="px-4 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-0"
//                                   >
//                                     <div className="font-medium text-sm">{company.companyname}</div>
//                                     <div className="text-xs text-gray-500">
//                                       ID: {company.companyid}
//                                       {company.contractid && ` | Contract: ${company.contractid}`}
//                                     </div>
//                                   </div>
//                                 ))}
//                               </div>
//                             )}
//                           </div>
//                           {walkinFormData.companyid && (
//                             <div className="mt-1 text-xs text-green-600">
//                               ✓ Selected: {walkinFormData.companyname}
//                             </div>
//                           )}
//                         </div>

//                         <div>
//                           <label className="block text-sm font-medium text-gray-700 mb-1">
//                             Contract ID/Discount code *
//                           </label>
//                           <div className="flex items-center gap-2">
//                             <FaIdCard className="text-gray-400" />
//                             <input
//                               type="text"
//                               name="contractid"
//                               value={walkinFormData.contractid}
//                               onChange={handleWalkinInputChange}
//                               className="w-full px-3 py-2 border border-gray-300 rounded-lg"
//                               placeholder="Enter contract ID"
//                             />
//                           </div>
//                         </div>
//                       </>
//                     )}
//                   </div>

//                   {/* Right Column - Booking Details */}
//                   <div className="space-y-4">
//                     <div className="flex justify-between items-center border-b pb-2">
//                       <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
//                         <FaCalendar className="h-5 w-5" />
//                         Booking Details
//                       </h3>
//                       <button
//                         onClick={clearAllFields}
//                         className="px-3 py-1 text-sm bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors flex items-center gap-1"
//                       >
//                         <FaTimes className="h-3 w-3" />
//                         Clear All
//                       </button>
//                     </div>

//                     <div className="grid grid-cols-2 gap-4">
//                       <div>
//                         <label className="block text-sm font-medium text-gray-700 mb-1">
//                           Check-in Date *
//                         </label>
//                         <input
//                           type="date"
//                           name="checkindate"
//                           value={walkinFormData.checkindate}
//                           onChange={handleWalkinInputChange}
//                           className="w-full px-3 py-2 border border-gray-300 rounded-lg"
//                         />
//                       </div>
//                       <div>
//                         <label className="block text-sm font-medium text-gray-700 mb-1">
//                           Check-out Date *
//                         </label>
//                         <input
//                           type="date"
//                           name="checkoutdate"
//                           value={walkinFormData.checkoutdate}
//                           onChange={handleWalkinInputChange}
//                           className="w-full px-3 py-2 border border-gray-300 rounded-lg"
//                         />
//                       </div>
//                     </div>

//                     <button
//                       onClick={checkAvailability}
//                       disabled={
//                         checkingAvailability ||
//                         !walkinFormData.checkindate ||
//                         !walkinFormData.checkoutdate
//                       }
//                       className={`w-full py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
//                         checkingAvailability ||
//                         !walkinFormData.checkindate ||
//                         !walkinFormData.checkoutdate
//                           ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
//                           : 'bg-black text-white hover:bg-gray-800'
//                       }`}
//                     >
//                       {checkingAvailability ? (
//                         <>
//                           <FaSpinner className="animate-spin" />
//                           Checking Availability...
//                         </>
//                       ) : (
//                         <>
//                           <FaSearch />
//                           Check Room Availability
//                         </>
//                       )}
//                     </button>
//                     <div>
//                       <label className="block text-sm font-medium text-gray-700 mb-1">
//                         Room Type *
//                       </label>
//                       <select
//                         name="room_type"
//                         value={walkinFormData.room_type}
//                         onChange={handleWalkinInputChange}
//                         className="w-full px-3 py-2 border border-gray-300 rounded-lg"
//                       >
//                         <option value="">Select room type</option>
//                         {availableRoomTypes.map((room) => {
//                           const availability = getRoomAvailabilityFromAPI(room.roomtypename);
//                           const isAvailable = availability
//                             ? availability.available_rooms > 0
//                             : true;
//                           return (
//                             <option
//                               key={room.roomtypeid}
//                               value={room.roomtypename}
//                               disabled={!isAvailable}
//                               className={!isAvailable ? 'text-gray-400' : ''}
//                             >
//                               {room.roomtypename}
//                               {availability && ` (${availability.available_rooms} available)`}
//                               {!availability && ' (Check availability)'}
//                             </option>
//                           );
//                         })}
//                       </select>
//                       {walkinFormData.room_type && availabilityData.length > 0 && (
//                         <p className="text-xs text-gray-500 mt-1">
//                           {(() => {
//                             const avail = getRoomAvailabilityFromAPI(walkinFormData.room_type);
//                             return avail
//                               ? `${avail.available_rooms} room(s) available for these dates`
//                               : 'Check availability for these dates';
//                           })()}
//                         </p>
//                       )}
//                     </div>

//                     {/* ✅ Show Room Type Price */}
//                     {walkinFormData.room_type && (
//                       <div className="flex items-center gap-2 p-2 bg-gray-50 border border-gray-200 rounded-lg">
//                         <FaRupeeSign className="text-gray-600" />
//                         <span className="text-sm text-gray-700">
//                           Room Price:
//                           <span className="font-bold text-gray-900 ml-1">
//                             ₹
//                             {(() => {
//                               const selectedRoom = availableRoomTypes.find(
//                                 (r) => r.roomtypename === walkinFormData.room_type
//                               );
//                               return selectedRoom ? selectedRoom.baseprice || 'N/A' : 'N/A';
//                             })()}
//                           </span>
//                           {availabilityData.length > 0 && (
//                             <span className="text-xs text-gray-500 ml-2">per night</span>
//                           )}
//                         </span>
//                       </div>
//                     )}

//                     <div className="grid grid-cols-2 gap-4">
//                       <div>
//                         <label className="block text-sm font-medium text-gray-700 mb-1">
//                           Number of Guests *
//                         </label>
//                         <input
//                           type="number"
//                           name="number_of_guests"
//                           value={walkinFormData.number_of_guests}
//                           onChange={handleWalkinInputChange}
//                           min="1"
//                           max="10"
//                           className="w-full px-3 py-2 border border-gray-300 rounded-lg"
//                         />
//                       </div>
//                       <div>
//                         <label className="block text-sm font-medium text-gray-700 mb-1">
//                           Quantity (Rooms)
//                         </label>
//                         <input
//                           type="number"
//                           name="quantity"
//                           value={walkinFormData.quantity}
//                           onChange={handleWalkinInputChange}
//                           min="1"
//                           max="5"
//                           className="w-full px-3 py-2 border border-gray-300 rounded-lg"
//                         />
//                       </div>
//                     </div>

//                     <div>
//                       <label className="block text-sm font-medium text-gray-700 mb-1">
//                         Special Requests
//                       </label>
//                       <textarea
//                         name="special_requests"
//                         value={walkinFormData.special_requests}
//                         onChange={handleWalkinInputChange}
//                         rows="2"
//                         className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none"
//                         placeholder="Any special requests or notes..."
//                       />
//                     </div>
//                   </div>
//                 </div>
//               ) : (
//                 // ✅ Success Response - No auto-refresh
//                 <div className="p-6 bg-green-50 border border-green-200 rounded-lg">
//                   <div className="flex items-start gap-3">
//                     <FaCheckCircle className="h-6 w-6 text-green-600 mt-0.5 flex-shrink-0" />
//                     <div className="flex-1">
//                       <h4 className="font-semibold text-green-800 text-lg mb-3">
//                         Reservation Created Successfully!
//                       </h4>
//                       <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
//                         <div className="space-y-2">
//                           <div>
//                             <span className="text-gray-600">Reservation ID:</span>
//                             <span className="ml-2 font-mono font-medium text-green-700">
//                               {walkinResponse.order_id}
//                             </span>
//                           </div>

//                           <div>
//                             <span className="text-gray-600">Room Type:</span>
//                             <span className="ml-2 text-green-700">{walkinResponse.room_type}</span>
//                           </div>
//                           <div>
//                             <span className="text-gray-600">Guest Type:</span>
//                             <span className="ml-2 text-green-700">{walkinResponse.guest_type}</span>
//                           </div>
//                           {walkinResponse.is_corporate && walkinResponse.company_name && (
//                             <div>
//                               <span className="text-gray-600">Company:</span>
//                               <span className="ml-2 text-green-700">
//                                 {walkinResponse.company_name}
//                               </span>
//                             </div>
//                           )}
//                         </div>
//                         <div className="space-y-2">
//                           <div>
//                             <span className="text-gray-600">Number Of Rooms:</span>
//                             <span className="ml-2 text-green-700">{walkinResponse.quantity}</span>
//                           </div>
//                           <div>
//                             <span className="text-gray-600">Final Amount:</span>
//                             <span className="ml-2 font-medium text-green-700">
//                               ₹{formatToTwoDecimal(walkinResponse.billing?.final_amount)} /
//                               (Including Taxes & Fees)
//                             </span>
//                           </div>
//                           <div>
//                             <span className="text-gray-600">Stay Duration:</span>
//                             <span className="ml-2 text-green-700">
//                               {walkinResponse.stay_details?.total_days || 0} nights
//                             </span>
//                           </div>
//                         </div>
//                       </div>
//                       <div className="mt-4 pt-3 border-t border-green-200">
//                         <p className="text-sm text-green-700 flex items-start gap-2">
//                           <FaInfoCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
//                           Click "Close" to refresh and view the new reservation
//                         </p>
//                       </div>
//                     </div>
//                   </div>
//                 </div>
//               )}
//             </div>

//             {/* Modal Footer */}
//             <div className="px-6 py-4 bg-white border-t border-gray-200 flex justify-end gap-3">
//               {!walkinResponse ? (
//                 <>
//                   <button
//                     onClick={() => {
//                       setShowWalkinModal(false);
//                       setWalkinResponse(null);
//                       setWalkinFormData({
//                         firstname: '',
//                         lastname: '',
//                         phonenumber: '',
//                         emailid: '',
//                         countrycode: '+91',
//                         clienttype: 'Leisure',
//                         companyid: '',
//                         companyname: '',
//                         contractid: '',
//                         country: 'India',
//                         checkindate: '',
//                         checkoutdate: '',
//                         room_type: '',
//                         number_of_guests: 1,
//                         quantity: 1,
//                         special_requests: '',
//                         gst_number: '',
//                       });
//                       setCompanySearchTerm('');
//                       setFilteredCompanies([]);
//                       setShowCompanyDropdown(false);
//                       setShowGuestSearch(false);
//                       setGuestSearchTerm('');
//                       setSelectedGuest(null);
//                       setShowAvailability(false);
//                       setAvailabilityData([]);
//                     }}
//                     className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
//                   >
//                     Cancel
//                   </button>
//                   <button
//                     onClick={handleWalkinSubmit}
//                     disabled={walkinLoading}
//                     className={`px-6 py-2 rounded-lg font-medium text-white transition-colors flex items-center gap-2 ${
//                       walkinLoading
//                         ? 'bg-gray-400 cursor-not-allowed'
//                         : 'bg-black hover:bg-gray-800'
//                     }`}
//                   >
//                     {walkinLoading ? (
//                       <>
//                         <FaSpinner className="animate-spin" />
//                         Creating...
//                       </>
//                     ) : (
//                       'Create Reservation'
//                     )}
//                   </button>
//                 </>
//               ) : (
//                 // ✅ Close button - refreshes only when clicked
//                 <button
//                   onClick={handleCloseModal}
//                   className="px-6 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors"
//                 >
//                   Close
//                 </button>
//               )}
//             </div>
//           </div>
//         </div>
//       )}

//       <ToastContainer autoClose={3000} hideProgressBar className="mt-12" />
//     </div>
//   );
// };

// export default ReservationSearch;
